import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { App as AntApp, ConfigProvider, GetProp, Steps, StepsProps, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { CompetencePageCheck } from "../../../../../app/contexts/CompetenceCheckContext";
import { useGetCalcDraft, useGetCalcRecordRequestResponse, useGetContract } from "../../../../../common/api/queries";
import { QueryKeys } from "../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../common/types";
import { FinancialSector } from "../../../../agent/enums";
import { ClientFormType } from "../../../../client/enums";
import { Client } from "../../../../client/types";
import { processClientsDataViolations } from "../../../../client/utils";
import { InsuranceContract, StandardVehicleInsurance } from "../../../../contract/types";
import { DASHBOARD_ROUTE_PATHS } from "../../../../dashboard/paths";
import { CALC_ROUTE_PATHS } from "../../../paths";
import { OperationType } from "../../../records/enums";
import { VehicleCalcRecordRequestResponse } from "../../../records/types";
import { CalcLoadingModal } from "../../components/CalcLoadingModal";
import { OperationStage } from "../../realty/enums";
import { CalcResult } from "../../types";
import { createCalcGenDataFromContract, isApplicableResult, isVehicleGenData } from "../../utils";
import { VEHICLE_CALC_HOLDER_INDEX_MAP, VehicleCalcWrapper } from "../components/calc/VehicleCalcWrapper";
import VehicleGenWrapper from "../components/gen/VehicleGenWrapper";
import { VehicleCalcResultsView } from "../components/result/VehicleCalcResultsView";
import { useVehicleCalcResults, VehicleCalcResultsProvider } from "../contexts";
import { VehicleOwnerRelation, VehiclePolicyHolderRelation } from "../enums";
import {
  VehicleCalc,
  VehicleCalcCrossSelling,
  VehicleCalcReinsurancesData,
  VehicleCalcResultData,
  VehicleFormClients,
  VehicleGen,
  VehicleGenForm,
} from "../types";
import {
  createVehicleCalcObjectFromCalcData,
  createVehicleCalcObjectFromGenData,
  createVehicleFormClientsObjectFromCalcData,
  createVehicleFormClientsObjectFromGenData,
  createVehicleGenFormDataObject,
} from "../utils";

type StepItem = {
  title: React.ReactNode;
  icon: React.ReactNode;
  content?: React.ReactNode;
};

enum VEHICLE_CALC_STEPS {
  FIRST_STEP_CALC = 0,
  SECOND_STEP_RESULTS = 1,
  THIRD_STEP_GENERATE_CONTRACT = 2,
}

export const VehicleCalcContainer = () => (
  <VehicleCalcResultsProvider>
    <VehicleCalcContainerContent />
  </VehicleCalcResultsProvider>
);

const VehicleCalcContainerContent = () => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const recordId = searchParams.get(QueryKeys.RECORD_ID);
  const draftId = searchParams.get(QueryKeys.DRAFT_ID);
  const contractId = searchParams.get(QueryKeys.CONTRACT_ID);

  const [currentStepIndex, setCurrentStepIndex] = useState(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
  const [lastAvailableStepIndex, setLastAvailableStepIndex] = useState(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
  const [selectedResult, setSelectedResult] = useState<CalcResult<VehicleCalcResultData>>();
  const [selectedVehicleVin, setSelectedVehicleVin] = useState<string>();
  const [calcData, setCalcData] = useState<VehicleCalc>();
  const [genData, setGenData] = useState<VehicleGenForm>();
  const [clients, setClients] = useState<VehicleFormClients>({
    holder: undefined,
    policyHolder: undefined,
    owner: undefined,
    representative: undefined,
  });

  const [calcDataFormErrors, setCalcDataFormErrors] = useState<FieldConstraintViolation[]>([]);
  const [calcClientsViolationErrors, setCalcClientsViolationErrors] = useState<
    Map<ClientFormType, FieldConstraintViolation[]>
  >(() => new Map());
  const [isCalcFormChanged, setIsCalcFormChanged] = useState(false);
  const [calculationCount, setCalculationCount] = useState(0);

  const { data: calcRecord, isError: isErrorCalcRecord } =
    useGetCalcRecordRequestResponse<VehicleCalcRecordRequestResponse>(recordId ?? "");
  const { data: calcDraft, isError: isErrorCalcDraft } = useGetCalcDraft(draftId ?? "");
  const { data: contract, isError: isErrorContract } = useGetContract(contractId ?? "");
  const { applicableResults, calculate, clearResults } = useVehicleCalcResults();

  useEffect(() => {
    if (isErrorCalcRecord || isErrorCalcDraft || isErrorContract) {
      clearResults();
      navigate(CALC_ROUTE_PATHS.vehicle.to, { replace: true });
    }
  }, [isErrorCalcRecord, isErrorCalcDraft, isErrorContract]);

  const initFormDataFromGenData = (genData: VehicleGen) => {
    const clients = createVehicleFormClientsObjectFromGenData(genData);

    setCalcData(createVehicleCalcObjectFromGenData(genData));
    setClients(clients);
    setGenData(createVehicleGenFormDataObject(genData, clients));
    setSelectedVehicleVin(genData.vehicleData.vin);
  };

  useEffect(() => {
    if (calcData || !contract) {
      return;
    }

    const calcGenData = createCalcGenDataFromContract(contract);

    if (!calcGenData || !isVehicleGenData(calcGenData)) {
      notification.error({
        title: t("common.error"),
        description: t("calc.helpers.loadDataError"),
        key: "loadCalcDataError",
      });

      clearResults();
      navigate(CALC_ROUTE_PATHS.vehicle.to, { replace: true });
      return;
    }

    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.helpers.calcDataInitFromContract"),
      duration: 10,
      key: "vehicleCalcDataInitFromContract",
    });

    initFormDataFromGenData(calcGenData);
  }, [contract]);

  useEffect(() => {
    if (calcData || !calcRecord) {
      return;
    }

    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.records.helpers.calcDataInit"),
      duration: 10,
      key: "vehicleCalcDataInit",
    });

    if (calcRecord.operationType === OperationType.CALCULATE) {
      setCalcData(createVehicleCalcObjectFromCalcData(calcRecord.request));
      setClients(createVehicleFormClientsObjectFromCalcData(calcRecord.request));
    } else {
      initFormDataFromGenData(calcRecord.request);
    }
  }, [calcRecord]);

  useEffect(() => {
    if (calcData || !calcDraft?.draftData) {
      return;
    }

    notification.info({
      title: t("calc.draft.helpers.draftLoaded"),
      description: t("calc.draft.helpers.draftLoadedInfo"),
      duration: 10,
      key: "vehicleDraftLoaded",
    });

    setCalcData(createVehicleCalcObjectFromGenData(calcDraft.draftData));
    const clients = createVehicleFormClientsObjectFromGenData(calcDraft.draftData);
    setClients(clients);

    if (calcDraft.stage === OperationStage.GENERATE) {
      setGenData(createVehicleGenFormDataObject(calcDraft.draftData, clients));
      setSelectedVehicleVin(calcDraft.draftData.vehicleData.vin);
    }
  }, [calcDraft?.draftData]);

  const handleClientChange = (type: ClientFormType, client?: Partial<Client>) => {
    let updatedClients: VehicleFormClients;
    switch (type) {
      case ClientFormType.HOLDER:
        updatedClients = { ...clients, holder: client };
        break;
      case ClientFormType.POLICY_HOLDER:
        updatedClients = { ...clients, policyHolder: client };
        break;
      case ClientFormType.OWNER:
        updatedClients = { ...clients, owner: client };
        break;
      case ClientFormType.REPRESENTATIVE:
        updatedClients = { ...clients, representative: client };
        break;
      default:
        updatedClients = { ...clients };
        break;
    }

    setClients(updatedClients);
    return updatedClients;
  };

  const handleResetCalculatorClick = (onSuccess: () => void) => {
    modal.confirm({
      title: t("calc.helpers.resetCalculatorSubmit"),
      okText: t("common.yes"),
      cancelText: t("common.back"),
      onOk: () => {
        setClients({ holder: undefined, policyHolder: undefined, owner: undefined, representative: undefined });
        setCalcData(undefined);
        setGenData(undefined);
        setSelectedResult(undefined);

        clearResults();
        onSuccess();
        navigate(CALC_ROUTE_PATHS.vehicle.to, { replace: true });
      },
    });
  };

  const handleCalculationFormSubmit = (calcData: VehicleCalc) => {
    setCalcData(calcData);
    calculate(calcData, {
      onSuccess: (results) => {
        setCalculationCount((count) => count + 1);
        setIsCalcFormChanged(false);
        setCalcDataFormErrors([]);
        discardSelectedResult();

        if (results.some(isApplicableResult)) {
          handleNextStep();
        } else if (results.length) {
          resetToCalcStep();
        }
      },
    });
  };

  const handleRecalculate = (reinsurancesData: VehicleCalcReinsurancesData, crossSelling: VehicleCalcCrossSelling) => {
    if (!calcData) {
      return;
    }

    const updatedCalcData = {
      ...calcData,
      reinsurancesData,
      generalData: { ...calcData.generalData, crossSelling },
    };

    calculate(updatedCalcData, {
      keepResults: true,
      onSuccess: () => {
        setCalcData(updatedCalcData);
        setCalcDataFormErrors([]);
        discardSelectedResult();
      },
    });
  };

  const discardSelectedResult = () => {
    setSelectedResult(undefined);
    setLastAvailableStepIndex(VEHICLE_CALC_STEPS.SECOND_STEP_RESULTS);
  };

  const handleReturnToCalculationClick = (genFormData: VehicleGenForm, contract: InsuranceContract) => {
    if (calcData) {
      const insurance = contract.insurances[0] as StandardVehicleInsurance;
      const { policyHolderRelation, ownerRelation } = calcData.clientsData;

      setSelectedResult(undefined);
      setGenData({
        ...genFormData,
        clientsData: {
          ...genFormData.clientsData,
          representativeIdentifier: undefined,
          representativeFunction: undefined,
        },
      });
      setClients({
        holder: contract.clients[insurance.vehicleHolderIndex],
        policyHolder:
          policyHolderRelation === VehiclePolicyHolderRelation.DIFFERENT_FROM_VEHICLE_HOLDER
            ? contract.clients[contract.policyHolderIndex]
            : undefined,
        owner:
          ownerRelation === VehicleOwnerRelation.DIFFERENT_FROM_BOTH
            ? contract.clients[insurance.vehicleOwnerIndex]
            : undefined,
        representative: undefined,
      });
    } else {
      setSelectedResult(undefined);
      setGenData(genFormData);
    }

    setCurrentStepIndex(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
    setLastAvailableStepIndex(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
  };

  const handleGenerateContractClick = (selectedResult: CalcResult<VehicleCalcResultData>) => {
    setSelectedResult(selectedResult);
    handleNextStep();
  };

  const handleShowErrors = (errors: FieldConstraintViolation[]) => {
    setCalcDataFormErrors(errors);

    if (errors.some((error) => error.fieldPath.startsWith("clientsData.clients"))) {
      setCalcClientsViolationErrors(
        processClientsDataViolations(VEHICLE_CALC_HOLDER_INDEX_MAP, "clientsData.clients", errors),
      );
    }

    goToPreviousStep();
  };

  const handleNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);

    if (lastAvailableStepIndex < currentStepIndex + 1) {
      setLastAvailableStepIndex(lastAvailableStepIndex + 1);
    }
  };

  const resetToCalcStep = () => {
    setCurrentStepIndex(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
    setLastAvailableStepIndex(VEHICLE_CALC_STEPS.FIRST_STEP_CALC);
  };

  const handleStepChange = (stepIndex: number) => {
    if (stepIndex === VEHICLE_CALC_STEPS.SECOND_STEP_RESULTS && applicableResults.length === 0) {
      resetToCalcStep();
    } else {
      setCurrentStepIndex(stepIndex);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const goToNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const stepItems: StepItem[] = [
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.calculation")}</div>,
      icon: <AntIcon type="laptop" />,
      content: (
        <VehicleCalcWrapper
          calcData={calcData}
          formErrors={calcDataFormErrors}
          clientsViolationErrors={calcClientsViolationErrors}
          setIsFormChanged={() => setIsCalcFormChanged(true)}
          holder={clients.holder}
          onCalculationFormSubmit={handleCalculationFormSubmit}
          onClientChange={handleClientChange}
          onClientsViolationErrorsChange={setCalcClientsViolationErrors}
          onResetCalculatorClick={handleResetCalculatorClick}
          goToCalcResults={isCalcFormChanged || applicableResults.length === 0 ? undefined : goToNextStep}
        />
      ),
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.insuranceSelection")}</div>,
      icon: <AntIcon type="file-search" />,
      content: calcData ? (
        <VehicleCalcResultsView
          key={calculationCount}
          calcData={calcData}
          handleGenerateContract={handleGenerateContractClick}
          handleShowErrors={handleShowErrors}
          handleRecalculate={handleRecalculate}
          goToPreviousStep={goToPreviousStep}
          clients={clients}
          draft={calcDraft}
        />
      ) : undefined,
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.contractGeneration")}</div>,
      icon: <AntIcon type="file-done" />,
      content:
        calcData && selectedResult ? (
          <VehicleGenWrapper
            initialData={genData}
            calcData={calcData}
            clients={clients}
            selectedResult={selectedResult}
            draftId={draftId ?? undefined}
            selectedVehicleVin={selectedVehicleVin}
            onReturnToCalculationClick={handleReturnToCalculationClick}
            onClientChange={handleClientChange}
            onSelectedVehicleVinSet={setSelectedVehicleVin}
            goToPreviousStep={goToPreviousStep}
          />
        ) : undefined,
    },
  ];

  const steps: GetProp<StepsProps, "items"> = stepItems.map((item, index) => ({
    key: index,
    title: item.title,
    disabled: index > lastAvailableStepIndex || isCalcFormChanged,
    status: currentStepIndex >= index ? "finish" : "wait",
    icon: item.icon,
  }));

  return (
    <DisplayWrapper itemLoaded={recordId || draftId || contractId ? !!calcData : true}>
      <CompetencePageCheck
        sector={FinancialSector.INSURANCES_OR_REINSURANCES}
        redirectTo={DASHBOARD_ROUTE_PATHS.homepage.to}
      >
        <ContentWrapper>
          <div style={{ maxWidth: 800, margin: "10px auto 20px" }}>
            <ConfigProvider theme={{ components: { Steps: { customIconFontSize: 28 } } }}>
              <Steps onChange={handleStepChange} current={currentStepIndex} items={steps} />
            </ConfigProvider>
          </div>

          <Tabs
            activeKey={currentStepIndex.toString()}
            renderTabBar={() => <></>}
            items={[
              {
                key: VEHICLE_CALC_STEPS.FIRST_STEP_CALC.toString(),
                label: undefined,
                children: stepItems[VEHICLE_CALC_STEPS.FIRST_STEP_CALC]?.content,
              },
              {
                key: VEHICLE_CALC_STEPS.SECOND_STEP_RESULTS.toString(),
                label: undefined,
                children: stepItems[VEHICLE_CALC_STEPS.SECOND_STEP_RESULTS]?.content,
              },
              {
                key: VEHICLE_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT.toString(),
                label: undefined,
                children: stepItems[VEHICLE_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT]?.content,
              },
            ]}
          />

          <CalcLoadingModal />
        </ContentWrapper>
      </CompetencePageCheck>
    </DisplayWrapper>
  );
};
